import { Link } from "react-router-dom";
import { MapPin, Route, Stamp, Radio, Check, ArrowRight } from "lucide-react";
import { PLANS } from "@/components/PlanPicker";

const FEATURES = [
  {
    icon: MapPin,
    kicker: "01 · Pin it",
    title: "Every place, pinned forever",
    body: "Tap the map, search a place, or just snap a photo — Pins reads the GPS hidden inside your pictures and drops the pin for you. Notes, dates and photographs turn each pin into a journal entry.",
    img: "/marketing/02-journal-map.jpg",
    alt: "The Pins journal and world map dashboard",
  },
  {
    icon: Route,
    kicker: "02 · Chart it",
    title: "Trips that draw themselves",
    body: "String pins into trips and watch a dashed route unfold across the atlas, stop by stop. Give each trip an album cover and your shelf becomes a library of journeys.",
    img: "/marketing/03-trip-route.jpg",
    alt: "A trip route drawn across the Iberian peninsula",
  },
  {
    icon: Stamp,
    kicker: "03 · Stamp it",
    title: "A passport that fills itself",
    body: "Every new country and US state presses a stamp into your passport book. Distance travelled, milestones, flags — your wandering, tallied without lifting a finger.",
    img: "/marketing/04-passport.jpg",
    alt: "The passport book with country stamps",
  },
  {
    icon: Radio,
    kicker: "04 · Share it",
    title: "Send it home, live",
    body: "Share any trip with a magic link — no account needed to view. Invite fellow travellers to add their own pins, or broadcast your live location so the people who love you can follow along.",
    img: "/illustrations/onboard-share.jpg",
    alt: "Postcards flying home over flat-colour mountains",
  },
];

const FREE_FEATURES = [
  "Unlimited pins, trips & journal entries",
  "Photo pinning with GPS from your camera",
  "Passport stamps, milestones & distance",
  "Magic share links + collaborators",
  "CSV / GPX export",
  "6 photos per pin · 10MB each",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1f1f1f]" data-testid="landing-page">
      {/* Nav */}
      <header className="border-b-2 border-[#1f1f1f] bg-[#f7f5f0] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Pins" className="w-9 h-9" />
            <div>
              <p className="font-serif text-xl leading-none">Pins</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#5c5c5c]">A travel journal</p>
            </div>
          </div>
          <nav className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="landing-login-link"
              className="font-mono text-[11px] uppercase tracking-[0.18em] underline underline-offset-4 text-[#2c4a3e] hover:text-[#1f1f1f]"
            >
              Log in
            </Link>
            <Link to="/register" data-testid="landing-nav-signup" className="btn-terracotta !py-2 !px-4 hidden sm:inline-flex">
              Start pinning
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b-2 border-[#1f1f1f]" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline text-[#b85c47]">pinmy.travel</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3">
              Pin the places
              <br />
              you've <em className="text-[#2c4a3e]">been.</em>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#5c5c5c] max-w-md leading-relaxed">
              A living atlas of everywhere you've walked, eaten, listened and lingered — pins, trips,
              passport stamps and the stories in between.
            </p>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link to="/register" data-testid="landing-hero-cta" className="btn-terracotta !px-6 !py-3 flex items-center gap-2 text-base">
                Sign up free <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c]">
                Free forever · no card needed
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="/illustrations/hero-landing.jpg"
              alt="A vintage travel poster of mountains with a dashed pin route winding to the horizon"
              className="w-full border-2 border-[#1f1f1f] shadow-[8px_8px_0_#1f1f1f]"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* Honest promise band (no fabricated stats) */}
      <section className="bg-[#2c4a3e] text-[#f7f5f0] border-b-2 border-[#1f1f1f]" data-testid="landing-promise">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="font-serif text-2xl sm:text-3xl italic">Track every place you've ever been.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#f7f5f0]/70">
            Your pins · your photos · your map — private by default
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="landing-features">
        <p className="overline text-[#b85c47]">What Pins does</p>
        <h2 className="font-serif text-base md:text-lg mt-1 text-[#5c5c5c]">Four ways to keep the road with you</h2>
        <div className="mt-10 space-y-16 lg:space-y-24">
          {FEATURES.map((f, i) => (
            <div
              key={f.kicker}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
              data-testid={`landing-feature-${i}`}
            >
              <img
                src={f.img}
                alt={f.alt}
                loading="lazy"
                className="w-full border-2 border-[#1f1f1f] shadow-[6px_6px_0_#1f1f1f] bg-white"
              />
              <div>
                <p className="overline text-[#b85c47] flex items-center gap-2">
                  <f.icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {f.kicker}
                </p>
                <h3 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight">{f.title}</h3>
                <p className="mt-3 text-[#5c5c5c] leading-relaxed max-w-md">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t-2 border-[#1f1f1f] bg-[#efebe1]" data-testid="landing-pricing">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <p className="overline text-[#b85c47]">Pricing</p>
          <h2 className="font-serif text-base md:text-lg mt-1 text-[#5c5c5c]">Free to wander. Pro to broadcast.</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {/* Free */}
            <div className="tactile-card bg-[#f7f5f0] p-6 flex flex-col" data-testid="pricing-free">
              <p className="overline">For every traveller</p>
              <h3 className="font-serif text-3xl mt-1">Free</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-serif text-4xl text-[#2c4a3e]">$0</span>
                <span className="font-mono text-xs text-[#5c5c5c]">forever</span>
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {FREE_FEATURES.map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm">
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2c4a3e]" strokeWidth={2} /> {x}
                  </li>
                ))}
              </ul>
              <Link to="/register" data-testid="pricing-free-cta" className="btn-outline mt-5 text-center">
                Start free
              </Link>
            </div>
            {/* Pro + Family from the live app catalog (stays in sync with real gating) */}
            {PLANS.map((plan) => (
              <div
                key={plan.key}
                className="tactile-card p-6 flex flex-col"
                style={plan.key === "family" ? { background: "#2c4a3e", color: "#f7f5f0" } : { background: "#f7f5f0" }}
                data-testid={`pricing-${plan.key}`}
              >
                <p className="overline" style={plan.key === "family" ? { color: "rgba(247,245,240,0.7)" } : {}}>{plan.tagline}</p>
                <h3 className="font-serif text-3xl mt-1">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1 flex-wrap">
                  <span className={`font-serif text-4xl ${plan.key === "family" ? "text-[#f7f5f0]" : "text-[#2c4a3e]"}`}>
                    {plan.monthly.price}
                  </span>
                  <span className={`font-mono text-xs ${plan.key === "family" ? "text-[#f7f5f0]/70" : "text-[#5c5c5c]"}`}>
                    /mo · or {plan.yearly.price}/yr
                  </span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {plan.features.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm">
                      <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${plan.key === "family" ? "text-[#b85c47]" : "text-[#2c4a3e]"}`} strokeWidth={2} />
                      {x}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid={`pricing-${plan.key}-cta`}
                  className={`mt-5 text-center ${plan.key === "family" ? "btn-terracotta" : "btn-primary"}`}
                >
                  Choose {plan.name}
                </Link>
              </div>
            ))}
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c] mt-5 text-center">
            Upgrade any time from inside the app · cancel whenever
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#1f1f1f] bg-[#1f1f1f] text-[#f7f5f0]" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="" className="w-8 h-8 bg-[#f7f5f0] p-0.5" />
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#f7f5f0]/70">
              Pins · pinmy.travel · a travel journal
            </p>
          </div>
          <nav className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.18em]">
            <Link to="/login" className="underline underline-offset-4 hover:text-[#b85c47]" data-testid="footer-login">Log in</Link>
            <Link to="/terms" className="underline underline-offset-4 hover:text-[#b85c47]" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="underline underline-offset-4 hover:text-[#b85c47]" data-testid="footer-privacy">Privacy</Link>
            <a href="mailto:hello@pinmy.travel" className="underline underline-offset-4 hover:text-[#b85c47]" data-testid="footer-contact">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
